// Engagement context broadcast (ported from _PERLab/apps/shell — router-agnostic).
//
// Dual broadcast mechanism:
//   1. CustomEvent on window (same-origin host shell consumers)
//   2. bridge.sendEngagementContext (iframes via postMessage, decoupled from auth.context)
//
// In the TanStack re-implementation the broadcast is driven by AppShell's
// URL→scope sync (useUrlScopeSync) instead of a useLocation effect inside the
// provider — keeping the ContextChain provider router-free + unit-testable.
// Diff guard via prevEng ref prevents spurious broadcasts on initial mount or
// non-engagement URL changes.

export interface EngagementChangedDetail {
  rg: string;
  eng: string;
  prevEng: string | null;
  epoch: number;
}

const EVENT_NAME = "engagement:changed";

// Module-level epoch counter. Increments ONLY when diff guard passes (so RX3
// semantics stay clean — every increment represents a real engagement change).
let epochCounter = 0;

export function nextEpoch(): number {
  epochCounter += 1;
  return epochCounter;
}

export function currentEpoch(): number {
  return epochCounter;
}

export function dispatchEngagementChanged(detail: EngagementChangedDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<EngagementChangedDetail>(EVENT_NAME, { detail }));
}

// React hook for same-origin consumers (host shell components).
// Iframe consumers should use bridge.onEngagementContext instead.
import { useEffect } from "react";

export function useEngagementChangedListener(
  cb: (detail: EngagementChangedDetail) => void,
): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: Event) => {
      const ce = e as CustomEvent<EngagementChangedDetail>;
      cb(ce.detail);
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, [cb]);
}
